import { usePermissao } from "@/utils/permissao";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge, TIPO_SEGURO_CORES } from "@/components/ui/cores";
import { EntityIcon } from "@/components/ui/cores";
import { PageHeader } from "@/components/ui/page-header";
import { Column, DataTable, Pagination } from "@/components/datatable/DataTable";
import { ExportarButton, ExportarPdfButton, TODOS_REGISTROS } from "@/components/datatable/ExportarButton";
import Filter from "@/components/datatable/Filter";
import { ActionButton, Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FichaService } from "@/services";
import { PageRequest, PaginationData } from "@/types/pagination";
import { EyeIcon, LoaderCircle, MessageCircle, Pencil, Plus, RefreshCw, Trash2Icon } from "lucide-react";
import { AvisarClienteDialog } from "./AvisarClienteDialog";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { getTipoEndossoLabel } from "@/types/enum/TipoEndosso";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { formatCurrency, formatPercent } from "@/utils/format";
import { Sort } from "@/types/sort";
import { SeguroLista } from "@/types/Seguro";
import { getTipoSeguroLabel } from "@/types/enum/TipoSeguro";
import { getTenant } from "@/utils/TenantUtils";
import { periodoRenovacao } from "@/utils/renovacao";
import { ErrorPage } from "../ErrorPage";
import { AxiosError } from "axios";


export const SeguroListPage = () => {
  const [data, setData] = useState<SeguroLista[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError>();
  const [renovando, setRenovando] = useState<SeguroLista>();
  const [avisando, setAvisando] = useState<SeguroLista>();
  const [excluindo, setExcluindo] = useState<SeguroLista>();
  const [removendo, setRemovendo] = useState(false);
  const ultimaBusca = useRef<Parameters<typeof onSearch>>([]);
  const navigate = useNavigate();
  const podeAlterar = usePermissao('SEGURO_WRITE');

  const columns: Column[] = [
    {field: 'id', show: false, label: 'ID'},
    {field: 'segurado.nome', show: true, label: 'Segurado', filter: true, sorted: true},
    {field: 'seguradora.nome', show: true, label: 'Seguradora', filter: true, sorted: true},
    {field: 'corretor.nome', show: false, label: 'Corretor', filter: true, sorted: true},
    {field: 'tipoSeguro', show: true, label: 'Tipo', filter: true, type: 'CUSTOM', exportValue: item => getTipoSeguroLabel(item.tipoSeguro), getValue: item => item.tipoSeguro && <Badge className={TIPO_SEGURO_CORES[item.tipoSeguro]?.badge}>{getTipoSeguroLabel(item.tipoSeguro)}</Badge>},
    {field: 'placa', show: true, label: 'Placa', filter: true},
    {field: 'vigenciaInicial', show: true, mobile: false, label: 'Vigência Inicial', filter: true, type: 'DATE', sorted: true},
    {field: 'vigenciaFinal', show: true, label: 'Vigência Final', filter: true, type: 'DATE', sorted: true},
    {field: 'renovado', show: false, label: 'Renovado', filter: true, type: 'BOOLEAN'},
    {field: 'situacao', show: true, label: 'Situação', type: 'CUSTOM', exportValue: item => item.renovado ? 'Renovado' : item.vigenciaFinal && situacao(diasParaVencer(item.vigenciaFinal)), getValue: item => <Situacao vigenciaFinal={item.vigenciaFinal} renovado={item.renovado} />},
    {field: 'premioLiquido', show: false, mobile: false, label: 'Prêmio Líquido', type: 'CURRENCY'},
    {field: 'percentualComissao', show: false, mobile: false, label: 'Comissão', type: 'CUSTOM', exportValue: item => item.premioLiquido != null && item.percentualComissao != null ? getComissaoLiquidaFicha(item.premioLiquido, item.percentualComissao).toLocaleString('pt-BR', { useGrouping: false }) : '', getValue: item => item.premioLiquido != null && item.percentualComissao != null && `${formatCurrency(getComissaoLiquidaFicha(item.premioLiquido, item.percentualComissao))} (${formatPercent(item.percentualComissao)})`},
    {field: 'endossos', show: false, mobile: false, label: 'Endossos', type: 'CUSTOM', exportValue: item => item.endossos?.length || 0, getValue: item => item.endossos?.length > 0 && <Badge className="bg-violet-500/10 text-violet-700 dark:text-violet-300" title={item.endossos.map((e: any) => getTipoEndossoLabel(e.tipo)).join(', ')}>{item.endossos.length} {item.endossos.length === 1 ? 'endosso' : 'endossos'}</Badge>},
  ]

  const getComissaoLiquidaFicha = (premioLiquido: number, porcentagemComissao: number) => {
    return (premioLiquido*porcentagemComissao)/100;
  }

  const excluir = () => {
    setRemovendo(true);
    FichaService.remove(excluindo!.id!)
      .then(() => {
        toast.success('Seguro excluído.');
        setExcluindo(undefined);
        onSearch(...ultimaBusca.current);
      })
      .catch(() => setExcluindo(undefined))
      .finally(() => setRemovendo(false));
  }

  const onSearch = (filterRsql?: string, pagination?: PageRequest, sort?: Sort) => {
    ultimaBusca.current = [filterRsql, pagination, sort];
    setLoading(true);
    FichaService.findLista(filterRsql, pagination, sort)
      .then(response => {
        setData(response.data.content)
        setPagination({
          ...response.data
        })
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }

  if (error){
    return <ErrorPage axiosError={error} />
  }

  return (
    <div className="space-y-4">
    <PageHeader icon={<EntityIcon entidade="seguro" />} title="Seguros" total={pagination?.totalElements}>
      <ExportarButton arquivo="seguros" columns={columns} buscar={() => FichaService.findLista(ultimaBusca.current[0], TODOS_REGISTROS, ultimaBusca.current[2])} />
      <ExportarPdfButton arquivo="seguros" titulo="Seguros" columns={columns} buscar={() => FichaService.findLista(ultimaBusca.current[0], TODOS_REGISTROS, ultimaBusca.current[2])} />
      {podeAlterar && <Button onClick={() => navigate('novo')}><Plus className="mr-2 h-4 w-4" /> Novo seguro</Button>}
    </PageHeader>
    <Card className="overflow-hidden shadow-none">
      <Filter columns={columns} onSearch={onSearch} defaultSort={{sort: ['segurado.nome,asc', 'vigenciaFinal,desc']}} loading={loading} />
      <DataTable values={data} loading={loading}
        columns={columns} 
        keyExtractor={(item: any, index: number) => item.id} 
        pagination={pagination}
        actions={(item: any) => {
          return (
            <>
            <ActionButton icon={<EyeIcon />} label="Visualizar" onClick={() => navigate(`${item.id}/resumo`)} />
            {!item.renovado && (
              <ActionButton icon={<MessageCircle />} label="Avisar cliente" onClick={() => setAvisando(item)} />
            )}
            {podeAlterar && (
              <>
              <ActionButton icon={<ExclamationTriangleIcon />} label="Novo sinistro" onClick={() => navigate(`${item.id}/sinistro/novo`)} />
              {!item.renovado && (
                <ActionButton icon={<RefreshCw />} label="Renovar" onClick={() => setRenovando(item)} />
              )}
              <ActionButton icon={<Pencil />} label="Editar" onClick={() => navigate(item.id)} />
              <ActionButton icon={<Trash2Icon />} label="Excluir" onClick={() => setExcluindo(item)} variant="destructive" />
              </>
            )}
            </>
          )
        }}
      />
      <Pagination pagination={pagination} />
    </Card>
    <AvisarClienteDialog seguro={avisando} onClose={() => setAvisando(undefined)} />
    <Dialog open={!!renovando} onOpenChange={open => !open && setRenovando(undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Renovar seguro?</DialogTitle>
          <DialogDescription>
            Será aberto um novo seguro de <span className="font-medium text-foreground">{renovando?.segurado?.nome}</span> com
            os mesmos dados e vigência de {vigenciaRenovacao(renovando?.vigenciaFinal as any)}. Confira as informações antes de salvar.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <DialogClose asChild>
            <Button variant="outline">Cancelar</Button>
          </DialogClose>
          <Button onClick={() => navigate(`novo?renovar=${renovando!.id}`)}>
            <RefreshCw className="mr-2 h-4 w-4" /> Renovar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={!!excluindo} onOpenChange={open => !open && !removendo && setExcluindo(undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Excluir seguro?</DialogTitle>
          <DialogDescription>
            O seguro de <span className="font-medium text-foreground">{excluindo?.segurado?.nome}</span>
            {excluindo?.placa && <> (placa {excluindo.placa})</>} e seus endossos serão excluídos definitivamente. Esta ação não pode ser desfeita.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <DialogClose asChild>
            <Button variant="outline" disabled={removendo}>Cancelar</Button>
          </DialogClose>
          <Button variant="destructive" onClick={excluir} disabled={removendo}>
            {removendo ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Trash2Icon className="mr-2 h-4 w-4" />} Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
  )
}

const vigenciaRenovacao = (vigenciaFinal?: string) => {
  const [inicio, fim] = periodoRenovacao(vigenciaFinal);
  return `${inicio.toLocaleDateString('pt-BR')} a ${fim.toLocaleDateString('pt-BR')}`;
}

// Dias até a vigência final (negativo = vencido)
const diasParaVencer = (vigenciaFinal: string) => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return Math.round((new Date(`${vigenciaFinal}T00:00:00`).getTime() - hoje.getTime()) / 86400000);
}

const situacao = (dias: number) => dias < 0
  ? 'Vencido'
  : dias <= 30
    ? (dias === 0 ? 'Vence hoje' : `Vence em ${dias} ${dias === 1 ? 'dia' : 'dias'}`)
    : 'Vigente';

// Renovado (já existe o seguro seguinte) / vigente / vence em até 30 dias / vencido, a partir da vigência final
const Situacao = ({ vigenciaFinal, renovado }: { vigenciaFinal?: string; renovado?: boolean }) => {
  if (!vigenciaFinal) return null;
  const dias = diasParaVencer(vigenciaFinal);
  const cor = renovado
    ? 'bg-blue-500/10 text-blue-700 dark:text-blue-300 [&>span]:bg-blue-500'
    : dias < 0
    ? 'bg-muted text-muted-foreground [&>span]:bg-muted-foreground/60'
    : dias <= 30
      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 [&>span]:bg-amber-500'
      : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 [&>span]:bg-emerald-500';
  const texto = renovado ? 'Renovado' : situacao(dias);
  return (
    <Badge className={`gap-1.5 ${cor}`}>
      <span aria-hidden className="h-1.5 w-1.5 rounded-full" />
      {texto}
    </Badge>
  );
}
